import { useState, useEffect, useRef, useCallback } from 'react'
import { format } from 'date-fns'
import {
  Reply, Forward, ExternalLink, Pencil, X, Send, Clock,
  Paperclip, ChevronDown, ChevronRight, Trash2,
} from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import DOMPurify from 'dompurify'
import toast from 'react-hot-toast'
import { EmailAttachmentViewerModal } from '../../components/EmailAttachmentViewerModal'
import { emailApi, type EmailMessage, type SendEmailPayload } from '../../api/email'
import { leadsApi } from '../../api/leads'
import { usersApi } from '../../api/settings'
import { useAuthStore } from '../../store/auth'
import { LeadSearchInput } from './LeadSearchInput'
import { SchedulePopover } from './SchedulePopover'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { cn } from '../../lib/utils'

interface Lead { id: string; firstName: string; lastName: string; email: string }

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

// ─── AttachmentThumb ─────────────────────────────────────────────────────────

interface ThumbProps {
  companyId: string
  messageId: string
  index: number
  filename: string
  mimeType: string
  size: number
  onOpen: () => void
}

function AttachmentThumb({ companyId, messageId, index, filename, mimeType, size, onOpen }: ThumbProps) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const isImage = mimeType.startsWith('image/')

  const fetchBlob = useCallback(async () => {
    try {
      const res = await emailApi.downloadAttachment(companyId, messageId, index)
      let blob: Blob
      if (mimeType === 'image/svg+xml') {
        const clean = DOMPurify.sanitize(await res.data.text(), {
          USE_PROFILES: { svg: true, svgFilters: true },
        })
        blob = new Blob([clean], { type: 'image/svg+xml' })
      } else {
        blob = res.data
      }
      setBlobUrl(URL.createObjectURL(blob))
    } catch {
      // file may not be on disk (old message before attachment storage was added)
    }
  }, [companyId, messageId, index])

  useEffect(() => {
    if (isImage) fetchBlob()
    return () => { setBlobUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null }) }
  }, [isImage, fetchBlob])

  if (isImage) {
    return (
      <button
        type="button"
        onClick={onOpen}
        className="w-20 h-20 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 hover:ring-2 hover:ring-primary-400 transition-all"
        title={filename}
      >
        {blobUrl
          ? <img src={blobUrl} alt={filename} className="w-full h-full object-cover" />
          : <div className="w-full h-full flex items-center justify-center"><Paperclip className="w-5 h-5 text-gray-300" /></div>
        }
      </button>
    )
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 rounded px-2.5 py-1.5 text-xs text-gray-700 transition-colors"
      title={`Preview / download ${filename}`}
    >
      <Paperclip className="w-3 h-3 text-gray-400 flex-shrink-0" />
      <span className="truncate max-w-[180px]">{filename}</span>
      <span className="text-gray-400 flex-shrink-0">{formatBytes(size)}</span>
    </button>
  )
}

// ─── MessageItem ─────────────────────────────────────────────────────────────

interface MessageItemProps {
  message: EmailMessage
  isExpanded: boolean
  onToggle: () => void
  currentUserId: string | null
  userNameMap: Record<string, string>
  isSalesUser: boolean
}

function MessageItem({ message, isExpanded, onToggle, currentUserId, userNameMap, isSalesUser }: MessageItemProps) {
  const { companyId } = useParams<{ companyId: string }>()
  const [viewerIndex, setViewerIndex] = useState<number | null>(null)
  const isSent = message.direction === 'sent'
  const senderLabel = isSent
    ? (message.sentBy && message.sentBy !== currentUserId
        ? (userNameMap[message.sentBy] ?? 'Team member')
        : 'Me')
    : null
  const fromLabel = isSalesUser
    ? (isSent ? (senderLabel ?? 'Me') : 'Customer')
    : (isSent ? `${senderLabel} → ${(message.toAddresses ?? []).join(', ')}` : message.fromAddress)

  const sanitized = message.bodyHtml
    ? DOMPurify.sanitize(message.bodyHtml, {
        // Core inline/block tags
        ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'b', 'i', 'u', 's', 'a', 'ul', 'ol', 'li',
          'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre', 'code', 'img',
          'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th', 'hr', 'span', 'div',
          // Email-specific tags that many clients use
          'font', 'center', 'caption', 'colgroup', 'col',
          // Common email structural tags (content kept, tag stripped)
          'header', 'footer', 'main', 'section', 'article', 'aside',
          'figure', 'figcaption', 'small', 'sub', 'sup', 'abbr',
        ],
        ALLOWED_ATTR: [
          // Standard
          'href', 'src', 'alt', 'title', 'class', 'style', 'id',
          // Image sizing
          'width', 'height',
          // Table layout (used heavily in newsletter emails)
          'align', 'valign', 'bgcolor', 'cellpadding', 'cellspacing', 'border',
          'colspan', 'rowspan',
          // Font tag attributes
          'color', 'face', 'size',
          // Link
          'target', 'rel',
        ],
        FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onmouseout',
          'onfocus', 'onblur', 'onchange', 'onsubmit'],
        // Keep data: URIs on img so mailparser-converted inline images render
        ALLOW_DATA_ATTR: false,
        FORCE_BODY: true,
      })
    : DOMPurify.sanitize((message.bodyText ?? '').replace(/\n/g, '<br>'))

  const plainPreview = (message.bodyText ?? '').replace(/<[^>]+>/g, '').trim().slice(0, 100)
  const attachments = message.attachments ?? []
  const images = attachments.filter(a => a.mimeType.startsWith('image/'))
  const nonImages = attachments.filter(a => !a.mimeType.startsWith('image/'))
  const hasImages = images.length > 0

  return (
    <div className={cn('border-b border-gray-100 last:border-b-0', isSent && 'bg-primary-50/20')}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-start gap-3 px-6 py-3 hover:bg-gray-50 text-left transition-colors">
        <span className="mt-0.5 text-gray-400 flex-shrink-0">
          {isExpanded
            ? <ChevronDown className="w-3.5 h-3.5" />
            : <ChevronRight className="w-3.5 h-3.5" />}
        </span>
        <div className="flex-1 min-w-0">
          <span className={cn('text-sm font-medium', isSent ? 'text-primary-700' : 'text-gray-900')}>
            {fromLabel}
          </span>
          {!isExpanded && plainPreview && (
            <span className="ml-2 text-xs text-gray-400 truncate">{plainPreview}…</span>
          )}
          {!isExpanded && attachments.length > 0 && (
            <span className="ml-2 inline-flex items-center gap-0.5 text-xs text-gray-400">
              <Paperclip className="w-3 h-3" />{attachments.length}
            </span>
          )}
        </div>
        <span className="text-xs text-gray-400 flex-shrink-0 ml-4">
          {format(new Date(message.createdAt), 'MMM d · HH:mm')}
        </span>
      </button>

      {isExpanded && (
        <div className="px-6 pb-5 pl-12">
          {isSent && (message.ccAddresses ?? []).length > 0 && (
            <p className="text-xs text-gray-500 mb-2">CC: {message.ccAddresses.join(', ')}</p>
          )}
          <div className="prose prose-sm max-w-none text-gray-700"
            dangerouslySetInnerHTML={{ __html: sanitized }} />

          {attachments.length > 0 && (
            <div className="mt-3 border-t border-gray-100 pt-3 space-y-2">
              {hasImages && (
                <div className="flex flex-wrap gap-2">
                  {images.map((a, _) => {
                    const realIndex = attachments.indexOf(a)
                    return (
                      <AttachmentThumb
                        key={realIndex}
                        companyId={companyId!}
                        messageId={message.id}
                        index={realIndex}
                        filename={a.filename}
                        mimeType={a.mimeType}
                        size={a.size}
                        onOpen={() => setViewerIndex(realIndex)}
                      />
                    )
                  })}
                </div>
              )}
              {nonImages.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {nonImages.map((a) => {
                    const realIndex = attachments.indexOf(a)
                    return (
                      <AttachmentThumb
                        key={realIndex}
                        companyId={companyId!}
                        messageId={message.id}
                        index={realIndex}
                        filename={a.filename}
                        mimeType={a.mimeType}
                        size={a.size}
                        onOpen={() => setViewerIndex(realIndex)}
                      />
                    )
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {viewerIndex !== null && (
        <EmailAttachmentViewerModal
          attachments={attachments}
          initialIndex={viewerIndex}
          companyId={companyId!}
          messageId={message.id}
          onClose={() => setViewerIndex(null)}
        />
      )}
    </div>
  )
}

// ─── InlineReplyForm ──────────────────────────────────────────────────────────

interface InlineFormProps {
  companyId: string
  mode: 'reply' | 'forward'
  originalMessage: EmailMessage
  refId: string | null
  refType: 'lead' | 'deal' | null
  draftId?: string
  initialBody?: string
  initialTo?: string
  onClose: () => void
  onSent: () => void
}

function InlineReplyForm({
  companyId, mode, originalMessage, refId, refType,
  draftId, initialBody, initialTo, onClose, onSent,
}: InlineFormProps) {
  const { user } = useAuthStore()
  const qc = useQueryClient()
  const isOwnerOrAdmin = user?.role !== 'sales_user'

  const [toAddress, setToAddress] = useState(
    initialTo ?? (mode === 'reply' ? originalMessage.fromAddress : ''),
  )
  const [forwardLead, setForwardLead] = useState<Lead | null>(null)
  const [body, setBody] = useState(initialBody ?? '')
  const [cc, setCc] = useState('')
  const [bcc, setBcc] = useState('')
  const [showCc, setShowCc] = useState(false)
  const [showBcc, setShowBcc] = useState(false)
  const [scheduledAt, setScheduledAt] = useState<string | null>(null)
  const [showSchedule, setShowSchedule] = useState(false)
  const [files, setFiles] = useState<File[]>([])
  const fileRef = useRef<HTMLInputElement>(null)

  const MAX_PER_FILE = 25 * 1024 * 1024
  // Extension-based check — more reliable than MIME types, which vary by OS/browser.
  // e.g. .md is text/plain on some systems and text/markdown on others — extension is consistent.
  const ALLOWED_EXTENSIONS = new Set([
    'png', 'jpg', 'jpeg', 'gif', 'webp', 'svg',
    'pdf',
    'mp4', 'webm', 'ogg',
    'doc', 'docx', 'xls', 'xlsx',
    'txt', 'csv',
    'zip',
  ])
  const fileExt = (f: File) => f.name.includes('.') ? f.name.split('.').pop()!.toLowerCase() : ''
  const fileErr = (f: File) =>
    f.size > MAX_PER_FILE
      ? 'exceeds 25 MB'
      : !ALLOWED_EXTENSIONS.has(fileExt(f))
        ? `".${fileExt(f) || f.name}" is not allowed`
        : null
  const hasFileError = files.some(f => fileErr(f) !== null)

  const [confirmDiscard, setConfirmDiscard] = useState(false)
  const draftIdRef = useRef<string | null>(draftId ?? null)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const toRef = useRef(toAddress)
  const bodyRef = useRef(body)
  const refIdRef = useRef(refId)
  const refTypeRef = useRef(refType)
  // Keep refs current every render
  toRef.current = toAddress
  bodyRef.current = body
  refIdRef.current = refId
  refTypeRef.current = refType

  const subject = mode === 'reply'
    ? (originalMessage.subject.startsWith('Re:') ? originalMessage.subject : `Re: ${originalMessage.subject}`)
    : `Fwd: ${originalMessage.subject}`

  useEffect(() => () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current) }, [])

  const triggerSave = () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(() => {
      if (!bodyRef.current.trim()) return
      const dto: Partial<SendEmailPayload> = {
        toAddresses: toRef.current ? [toRef.current] : [],
        subject,
        bodyText: bodyRef.current,
        threadId: originalMessage.threadId ?? originalMessage.id,
        refId: refIdRef.current ?? undefined,
        refType: refTypeRef.current ?? undefined,
      }
      if (draftIdRef.current) {
        emailApi.updateDraft(companyId, draftIdRef.current, dto).catch(() => {})
      } else {
        emailApi.saveDraft(companyId, dto).then(r => {
          draftIdRef.current = r.data.id
          qc.invalidateQueries({ queryKey: ['inbox', companyId] })
        }).catch(() => {})
      }
    }, 2000)
  }

  const mutation = useMutation({
    mutationFn: async () => {
      if (!isOwnerOrAdmin && !refId) {
        throw new Error('This email has no linked lead. Ask an admin to link it first.')
      }
      const dto: SendEmailPayload = {
        toAddresses: [toAddress],
        ccAddresses: cc ? [cc] : undefined,
        bccAddresses: bcc ? [bcc] : undefined,
        subject,
        bodyText: body,
        threadId: originalMessage.threadId ?? originalMessage.id,
        sendAt: scheduledAt ?? undefined,
        refId: refId ?? undefined,
        refType: refType ?? undefined,
      }
      if (refId && refType) return emailApi.send(companyId, refType, refId, dto, files)
      return emailApi.sendUnlinked(companyId, dto, files)
    },
    onSuccess: () => {
      if (draftIdRef.current) {
        emailApi.deleteDraft(companyId, draftIdRef.current).catch(() => {})
        draftIdRef.current = null
      }
      toast.success(scheduledAt ? 'Email scheduled' : 'Email sent')
      qc.invalidateQueries({ queryKey: ['inbox', companyId] })
      qc.invalidateQueries({ queryKey: ['thread', companyId, originalMessage.threadId ?? originalMessage.id] })
      onSent()
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message ?? err?.message
      toast.error(typeof msg === 'string' ? msg : 'Failed to send email')
    },
  })

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 bg-gray-50 flex-shrink-0">
        <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
          {mode === 'reply' ? (isOwnerOrAdmin ? `Reply to ${originalMessage.fromAddress}` : 'Reply') : 'Forward'}
        </span>
        <div className="flex items-center gap-3">
          {!showCc && (
            <button type="button" onClick={() => setShowCc(true)} className="text-xs text-primary-600 hover:underline">
              + CC
            </button>
          )}
          {!showBcc && (
            <button type="button" onClick={() => setShowBcc(true)} className="text-xs text-primary-600 hover:underline">
              + BCC
            </button>
          )}
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600" title="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Form body */}
      <div className="flex-1 overflow-y-auto px-4 py-2 space-y-2">
        {mode === 'forward' && (
          <div>
            <label className="label">To *</label>
            {isOwnerOrAdmin ? (
              <input
                type="email"
                value={toAddress}
                onChange={e => { setToAddress(e.target.value); triggerSave() }}
                className="input"
                placeholder="Recipient email"
              />
            ) : (
              <LeadSearchInput
                companyId={companyId}
                value={forwardLead}
                onChange={lead => {
                  setForwardLead(lead)
                  setToAddress(lead?.email ?? '')
                  triggerSave()
                }}
                placeholder="Select a lead…"
                hideEmail
              />
            )}
          </div>
        )}
        {showCc && (
          <div>
            <label className="label">CC</label>
            <input type="email" value={cc} onChange={e => setCc(e.target.value)} className="input" />
          </div>
        )}
        {showBcc && (
          <div>
            <label className="label">BCC</label>
            <input type="email" value={bcc} onChange={e => setBcc(e.target.value)} className="input" />
          </div>
        )}
        <textarea
          value={body}
          onChange={e => { setBody(e.target.value); triggerSave() }}
          rows={6}
          className="input resize-none font-mono text-sm w-full"
          placeholder={mode === 'reply' ? 'Write your reply…' : 'Write your message…'}
          autoFocus
        />
        {files.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {files.map((f, i) => {
              const err = fileErr(f)
              return (
                <div
                  key={i}
                  className={`flex items-center gap-1.5 rounded px-2.5 py-1.5 text-xs ${err ? 'bg-red-50 text-red-700 ring-1 ring-red-300' : 'bg-gray-100 text-gray-700'}`}
                >
                  <Paperclip className={`w-3 h-3 flex-shrink-0 ${err ? 'text-red-400' : 'text-gray-400'}`} />
                  <span className="truncate max-w-[160px]">{f.name}</span>
                  <span className={`flex-shrink-0 ${err ? 'text-red-400' : 'text-gray-400'}`}>{formatBytes(f.size)}</span>
                  {err && <span className="flex-shrink-0 font-medium">· {err}</span>}
                  <button
                    type="button"
                    onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))}
                    className={`ml-0.5 flex-shrink-0 leading-none ${err ? 'text-red-400 hover:text-red-600' : 'text-gray-400 hover:text-red-500'}`}
                    title="Remove"
                  >×</button>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Footer — no Discard button; draft auto-saves */}
      <div className="border-t border-gray-200 px-4 py-2 flex items-center gap-2 bg-gray-50 flex-shrink-0">
        <button
          type="button"
          disabled={mutation.isPending || (mode === 'forward' && !toAddress.trim()) || hasFileError}
          onClick={() => mutation.mutate()}
          className="btn btn-primary btn-sm">
          <Send className="w-3.5 h-3.5" />
          {scheduledAt ? 'Schedule' : mutation.isPending ? 'Sending…' : mode === 'reply' ? 'Reply' : 'Forward'}
        </button>

        <div className="relative">
          <button
            type="button"
            onClick={() => setShowSchedule(s => !s)}
            className="btn btn-ghost btn-sm p-1.5"
            title="Schedule send">
            <Clock className="w-4 h-4 text-gray-500" />
          </button>
          {showSchedule && (
            <SchedulePopover
              onSchedule={iso => { setScheduledAt(iso); setShowSchedule(false) }}
              onClose={() => setShowSchedule(false)}
            />
          )}
        </div>

        {scheduledAt && (
          <button type="button" onClick={() => setScheduledAt(null)} className="text-xs text-red-500 hover:underline">
            Remove schedule
          </button>
        )}

        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="btn btn-ghost btn-sm p-1.5"
          title="Attach file">
          <Paperclip className="w-4 h-4 text-gray-500" />
        </button>
        <input
          ref={fileRef}
          type="file"
          multiple
          className="hidden"
          onChange={e => {
            setFiles(prev => [...prev, ...Array.from(e.target.files ?? [])])
            e.target.value = ''
          }}
        />

        <button
          type="button"
          onClick={() => setConfirmDiscard(true)}
          className="btn btn-ghost btn-sm text-red-400 hover:text-red-600 ml-auto"
          title="Discard draft">
          <Trash2 className="w-3.5 h-3.5" />
          Discard
        </button>
      </div>

      <ConfirmDialog
        isOpen={confirmDiscard}
        onClose={() => setConfirmDiscard(false)}
        onConfirm={() => {
          if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
          if (draftIdRef.current) {
            emailApi.deleteDraft(companyId, draftIdRef.current).catch(() => {})
            draftIdRef.current = null
          }
          setConfirmDiscard(false)
          onClose()
        }}
        title="Discard draft?"
        message="This reply draft will be permanently deleted."
        confirmLabel="Discard"
      />
    </div>
  )
}

// ─── ReadingPane ──────────────────────────────────────────────────────────────

interface Props {
  message: EmailMessage | null
}

export function ReadingPane({ message }: Props) {
  const { companyId } = useParams<{ companyId: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { user } = useAuthStore()

  const isSalesUser = user?.role === 'sales_user'

  const { data: companyUsers = [] } = useQuery({
    queryKey: ['company-users', companyId],
    queryFn: () => usersApi.list(companyId!).then(r => r.data),
    enabled: !!companyId,
    staleTime: 300_000,
  })
  const userNameMap: Record<string, string> = Object.fromEntries(
    (companyUsers as any[]).map(u => [u.id, `${u.firstName} ${u.lastName}`])
  )

  const [replyMode, setReplyMode] = useState<'reply' | 'forward' | null>(null)
  const [replyDraftId, setReplyDraftId] = useState<string | null>(null)
  const [replyDraftBody, setReplyDraftBody] = useState('')
  const [replyDraftTo, setReplyDraftTo] = useState('')

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  const [linkingLead, setLinkingLead] = useState(false)
  const [localRefId, setLocalRefId] = useState<string | null>(null)
  const [localRefType, setLocalRefType] = useState<'lead' | 'deal' | null>(null)
  const [linkedLeadObj, setLinkedLeadObj] = useState<Lead | null>(null)

  const isDraftEdit = message?.status === 'draft' && !!message?.threadId
  const threadId = message?.threadId ?? null

  useEffect(() => {
    if (!message) {
      setReplyMode(null); setReplyDraftId(null); setReplyDraftBody(''); setReplyDraftTo('')
      setExpandedIds(new Set()); setLocalRefId(null); setLocalRefType(null)
      setLinkedLeadObj(null); setLinkingLead(false)
      return
    }
    setLocalRefId(message.refId)
    setLocalRefType(message.refType)
    setLinkedLeadObj(null)
    setLinkingLead(false)
    setExpandedIds(new Set([message.id]))

    if (isDraftEdit) {
      const mode = message.subject?.toLowerCase().startsWith('fwd:') ? 'forward' : 'reply'
      setReplyMode(mode)
      setReplyDraftId(message.id)
      setReplyDraftBody(message.bodyText ?? '')
      setReplyDraftTo((message.toAddresses ?? [])[0] ?? '')
    } else {
      setReplyMode(null); setReplyDraftId(null); setReplyDraftBody(''); setReplyDraftTo('')
    }
  }, [message?.id])

  // Fetch full thread
  const { data: threadMessages } = useQuery({
    queryKey: ['thread', companyId, threadId],
    queryFn: () => emailApi.getThread(companyId!, threadId!).then(r => r.data),
    enabled: !!threadId && !!companyId,
    staleTime: 30_000,
    refetchInterval: 60_000,
  })

  // Auto-expand last message in thread when it loads
  useEffect(() => {
    if (threadMessages && threadMessages.length > 0) {
      const lastId = threadMessages[threadMessages.length - 1].id
      setExpandedIds(ids => new Set([...ids, lastId]))
    }
  }, [threadMessages])

  // Mark all unread received messages in thread as read
  useEffect(() => {
    if (!threadMessages || !companyId) return
    const unread = threadMessages.filter(m => m.direction === 'received' && !m.isRead)
    if (unread.length === 0) return
    Promise.all(unread.map(m => emailApi.markRead(companyId, m.id))).then(() => {
      qc.invalidateQueries({ queryKey: ['inbox', companyId] })
      qc.invalidateQueries({ queryKey: ['email-unread-count', companyId] })
    }).catch(() => {})
  }, [threadMessages])

  // Also mark the selected message as read immediately (before thread loads)
  useEffect(() => {
    if (!message || message.direction !== 'received' || message.isRead || !companyId) return
    emailApi.markRead(companyId, message.id).then(() => {
      qc.invalidateQueries({ queryKey: ['inbox', companyId] })
      qc.invalidateQueries({ queryKey: ['email-unread-count', companyId] })
    }).catch(() => {})
  }, [message?.id])

  // Lead name query
  const { data: leadData } = useQuery({
    queryKey: ['lead', companyId, localRefId],
    queryFn: () => leadsApi.get(companyId!, localRefId!).then(r => r.data),
    enabled: localRefType === 'lead' && !!localRefId,
    staleTime: 60_000,
  })
  const leadName = linkedLeadObj
    ? `${linkedLeadObj.firstName} ${linkedLeadObj.lastName}`
    : (leadData ? `${(leadData as any).firstName} ${(leadData as any).lastName}` : null)

  // Update ref mutation
  const updateRefMutation = useMutation({
    mutationFn: ({ refId, refType }: { refId: string | null; refType: 'lead' | 'deal' | null }) =>
      emailApi.updateRef(companyId!, message!.id, refId, refType),
    onSuccess: (_, vars) => {
      setLocalRefId(vars.refId)
      setLocalRefType(vars.refType)
      qc.invalidateQueries({ queryKey: ['inbox', companyId] })
    },
    onError: () => toast.error('Failed to update lead link'),
  })

  const handleLinkLead = (lead: Lead | null) => {
    updateRefMutation.mutate({ refId: lead?.id ?? null, refType: lead ? 'lead' : null })
    setLinkedLeadObj(lead)
    setLinkingLead(false)
  }

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  if (!message) {
    return (
      <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
        Select a message to read
      </div>
    )
  }

  // Messages to show in thread area (draft itself is excluded by getThread; shown in form instead)
  const displayMessages = threadMessages ?? (message.status !== 'draft' ? [message] : [])

  // Subject to show in header (strip "Re:"/"Fwd:" for cleaner display)
  const threadSubject = (message.subject ?? '')
    .replace(/^(Re:\s*)+/i, '').replace(/^(Fwd:\s*)+/i, '') || message.subject

  // "Original message" for the reply form: the last received message in thread
  const lastReceived = [...displayMessages].reverse().find(m => m.direction === 'received')
  const replyOriginalMessage = lastReceived ?? message

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Thread area — top half when replying, full height when reading */}
      <div className={cn('flex flex-col overflow-hidden', replyMode ? 'h-1/2' : 'flex-1')}>

        {/* Thread header */}
        <div className="px-6 py-3 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="flex items-start justify-between gap-4">
            <h2 className="text-base font-semibold text-gray-900 leading-snug">{threadSubject}</h2>
            {displayMessages.length > 1 && (
              <span className="text-xs text-gray-400 flex-shrink-0 mt-0.5">
                {displayMessages.length} messages
              </span>
            )}
          </div>

          {/* Lead linking */}
          <div className="mt-2 flex items-center gap-2 min-h-[22px]">
            {linkingLead ? (
              <>
                <div className="flex-1 max-w-xs">
                  <LeadSearchInput
                    companyId={companyId!}
                    value={null}
                    onChange={handleLinkLead}
                    placeholder="Search leads…"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setLinkingLead(false)}
                  className="text-xs text-gray-400 hover:text-gray-600">
                  Cancel
                </button>
              </>
            ) : localRefId ? (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => navigate(`/company/${companyId}/${localRefType}s/${localRefId}`)}
                  className="inline-flex items-center gap-1 text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full hover:bg-primary-200">
                  <ExternalLink className="w-3 h-3" />
                  {leadName ?? (localRefType === 'lead' ? 'Lead' : 'Deal')}
                </button>
                <button
                  type="button"
                  onClick={() => setLinkingLead(true)}
                  className="p-0.5 text-gray-400 hover:text-primary-600 rounded"
                  title="Change linked lead">
                  <Pencil className="w-3 h-3" />
                </button>
                <button
                  type="button"
                  onClick={() => handleLinkLead(null)}
                  className="p-0.5 text-gray-400 hover:text-red-500 rounded"
                  title="Remove lead link">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setLinkingLead(true)}
                className="text-xs text-primary-600 hover:text-primary-700 hover:underline">
                Link to a lead
              </button>
            )}
          </div>
        </div>

        {/* Message list — scrollable */}
        <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
          {displayMessages.map(msg => (
            <MessageItem
              key={msg.id}
              message={msg}
              isExpanded={expandedIds.has(msg.id)}
              onToggle={() => toggleExpand(msg.id)}
              currentUserId={user?.id ?? null}
              userNameMap={userNameMap}
              isSalesUser={isSalesUser}
            />
          ))}
          {displayMessages.length === 0 && isDraftEdit && (
            <div className="px-6 py-4 text-sm text-gray-400">No previous messages in this thread.</div>
          )}
        </div>

        {/* Action bar — only when not in reply/forward mode */}
        {!replyMode && (
          <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 flex gap-2 flex-shrink-0">
            <button
              onClick={() => { setReplyMode('reply'); setReplyDraftId(null); setReplyDraftBody(''); setReplyDraftTo('') }}
              className="btn btn-primary btn-sm">
              <Reply className="w-3.5 h-3.5" /> Reply
            </button>
            <button
              onClick={() => { setReplyMode('forward'); setReplyDraftId(null); setReplyDraftBody(''); setReplyDraftTo('') }}
              className="btn btn-ghost btn-sm">
              <Forward className="w-3.5 h-3.5" /> Forward
            </button>
          </div>
        )}
      </div>

      {/* Reply/forward form — bottom half */}
      {replyMode && (
        <div className="h-1/2 border-t border-gray-200 overflow-hidden flex flex-col">
          <InlineReplyForm
            companyId={companyId!}
            mode={replyMode}
            originalMessage={replyOriginalMessage}
            refId={localRefId}
            refType={localRefType}
            draftId={replyDraftId ?? undefined}
            initialBody={replyDraftBody || undefined}
            initialTo={replyDraftTo || undefined}
            onClose={() => { setReplyMode(null); setReplyDraftId(null) }}
            onSent={() => {
              setReplyMode(null); setReplyDraftId(null)
              qc.invalidateQueries({ queryKey: ['thread', companyId, threadId] })
            }}
          />
        </div>
      )}
    </div>
  )
}
